"use client";

import {
  hasReferralActivationCapabilities,
  PhoenixHttpError,
  type RegisterIxInstruction,
} from "@ellipsis-labs/rise";
import { base64 } from "@scure/base";
import {
  AccountRole,
  address,
  appendTransactionMessageInstructions,
  compileTransaction,
  createTransactionMessage,
  getTransactionEncoder,
  pipe,
  setTransactionMessageFeePayer,
  setTransactionMessageLifetimeUsingBlockhash,
  type Instruction,
} from "@solana/kit";

import { getRiseClient } from "@/lib/rise/client";
import { getSolanaRpc } from "@/lib/tx/rpc";
import { confirmSignature, signatureFromString } from "@/lib/tx/submit";
import type { SolanaSigner } from "@/lib/tx/useSolanaWallet";

import { TRADER_REGISTRATION_MAX_POSITIONS } from "./traderRegistrationFunding";

export interface TraderActivation {
  isPhoenixTrader: boolean;
}

const pendingChecks = new Map<string, Promise<TraderActivation>>();
const TRADER_PDA_INDEX = 0;
const TRADER_SUBACCOUNT_INDEX = 0;
const INVALID_REFERRAL_CODE_MESSAGE = "Invalid referral code";

const isInvalidCodeError = (error: unknown): boolean => {
  if (error instanceof PhoenixHttpError) {
    return error.code === "INVALID_CODE";
  }
  return error instanceof Error && error.message.includes("INVALID_CODE");
};

const normalizeActivationError = (error: unknown): Error => {
  if (isInvalidCodeError(error)) {
    return new Error(INVALID_REFERRAL_CODE_MESSAGE);
  }
  if (error instanceof Error) return error;
  return new Error("Activation failed");
};

const isNotFoundError = (error: unknown): boolean => {
  if (error === null || typeof error !== "object") return false;
  const status = (error as { status?: unknown }).status;
  if (status === 404) return true;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== "string") return false;
  const lower = message.toLowerCase();
  return lower.includes("not found") || lower.includes("does not exist");
};

const checkTraderState = async (authority: string): Promise<boolean> => {
  try {
    const state = await getRiseClient()
      .api.traders()
      .getTraderStateSnapshot(authority, {
        traderPdaIndex: TRADER_PDA_INDEX,
      });
    return (
      hasReferralActivationCapabilities(state.snapshot.capabilities.flags) &&
      state.snapshot.subaccounts.some(
        (subaccount) => subaccount.subaccountIndex === TRADER_SUBACCOUNT_INDEX
      )
    );
  } catch (error) {
    if (isNotFoundError(error)) return false;
    throw error;
  }
};

const checkTraderActivationInternal = async (
  authority: string
): Promise<TraderActivation> => {
  const client = getRiseClient();

  try {
    const wallet = await client.api.invite().checkWallet(authority);
    if (wallet.whitelisted) return { isPhoenixTrader: true };
  } catch {
    // Fall through to trader-state lookup. Some environments have different
    // invite service availability, but trader state is the final source we need.
  }

  return { isPhoenixTrader: await checkTraderState(authority) };
};

export const checkTraderActivation = (
  authority: string
): Promise<TraderActivation> => {
  const pending = pendingChecks.get(authority);
  if (pending !== undefined) return pending;

  const run = checkTraderActivationInternal(authority).finally(() => {
    if (pendingChecks.get(authority) === run) {
      pendingChecks.delete(authority);
    }
  });
  pendingChecks.set(authority, run);
  return run;
};

const getAccountRole = (
  account: RegisterIxInstruction["keys"][number]
): AccountRole => {
  if (account.isSigner) {
    return account.isWritable
      ? AccountRole.WRITABLE_SIGNER
      : AccountRole.READONLY_SIGNER;
  }
  return account.isWritable ? AccountRole.WRITABLE : AccountRole.READONLY;
};

const toInstruction = (ix: RegisterIxInstruction): Instruction => ({
  programAddress: address(ix.programId),
  accounts: ix.keys.map((account) => ({
    address: address(account.pubkey),
    role: getAccountRole(account),
  })),
  data: Uint8Array.from(ix.data),
});

const assertSignerMatchesAuthority = (
  authority: string,
  signer: SolanaSigner
): void => {
  if (signer.address !== authority) {
    throw new Error(
      "Connected wallet changed. Please reconnect and try again."
    );
  }
};

const buildSignedRegisterTransaction = async (
  signer: SolanaSigner,
  instructions: readonly RegisterIxInstruction[]
): Promise<string> => {
  if (instructions.length === 0) {
    throw new Error("Phoenix registration returned no instructions");
  }

  const rpc = getSolanaRpc();
  const latestBlockhash = await rpc
    .getLatestBlockhash({ commitment: "confirmed" })
    .send();
  const feePayer = address(signer.address);
  const message = pipe(
    createTransactionMessage({ version: 0 }),
    (m) => setTransactionMessageFeePayer(feePayer, m),
    (m) =>
      setTransactionMessageLifetimeUsingBlockhash(latestBlockhash.value, m),
    (m) =>
      appendTransactionMessageInstructions(instructions.map(toInstruction), m)
  );
  const transaction = compileTransaction(message);
  const unsignedBytes = new Uint8Array(
    getTransactionEncoder().encode(transaction)
  );
  const signedBytes = await signer.signTransaction(unsignedBytes);
  return base64.encode(signedBytes);
};

const activateWithReferralCode = async (
  authority: string,
  referralCode: string,
  signer: SolanaSigner
): Promise<void> => {
  const client = getRiseClient();
  const rpc = getSolanaRpc();
  const latestBlockhash = await rpc
    .getLatestBlockhash({ commitment: "confirmed" })
    .send();

  try {
    const built = await client.api.invite().buildActivateReferralTxRequest({
      referralCode,
      traderAuthority: authority,
      traderPdaIndex: TRADER_PDA_INDEX,
      traderSubaccountIndex: TRADER_SUBACCOUNT_INDEX,
      recentBlockhash: latestBlockhash.value.blockhash,
      lastValidBlockHeight: latestBlockhash.value.lastValidBlockHeight,
      registerTraderMaxPositions: BigInt(TRADER_REGISTRATION_MAX_POSITIONS),
      rpc,
      feePayer: signer.address,
      signTransaction: async (_transaction, context) =>
        signer.signTransaction(context.unsignedTransactionBytes),
    });

    const response = await client.api
      .invite()
      .activateReferralTx(built.request);
    if (response.signature !== undefined) {
      await confirmSignature(signatureFromString(response.signature));
    }
  } catch (error) {
    throw normalizeActivationError(error);
  }
};

const activateWithoutReferralCode = async (
  authority: string,
  signer: SolanaSigner
): Promise<void> => {
  const client = getRiseClient();
  const built = await client.api.exchange().buildRegisterIxs({
    traderAuthority: authority,
    txFeePayer: signer.address,
    maxPositions: TRADER_REGISTRATION_MAX_POSITIONS,
  });
  const transaction = await buildSignedRegisterTransaction(
    signer,
    built.instructions
  );
  const response = await client.api.exchange().sendRegisterIxs({
    transaction,
    traderAuthority: authority,
    txFeePayer: signer.address,
    maxPositions: built.maxPositions,
    traderPdaIndex: TRADER_PDA_INDEX,
    traderSubaccountIndex: TRADER_SUBACCOUNT_INDEX,
  });

  await confirmSignature(signatureFromString(response.signature));
};

export const activateTrader = async ({
  authority,
  referralCode,
  signer,
}: {
  authority: string;
  referralCode?: string;
  signer: SolanaSigner;
}): Promise<void> => {
  assertSignerMatchesAuthority(authority, signer);
  const trimmedReferralCode = referralCode?.trim() ?? "";
  if (trimmedReferralCode.length > 0) {
    await activateWithReferralCode(authority, trimmedReferralCode, signer);
    return;
  }

  await activateWithoutReferralCode(authority, signer);
};
